import { createSlice, isAnyOf } from '@reduxjs/toolkit';

import { validateLatLong, hashForKey } from 'utils/utils';
import { userLoggedIn } from 'store/appThunks';
import { LOCATION_SLICE_NAME } from 'store/slices/slicesNames';
import { updateStoreFromUrl, clearSearchForm, startOver } from 'store/appActions';
import { specialtySearchInPg, goToHomepage } from 'store/slices/chat/chatSlice';
import { executeSearch } from '../results/resultsThunks';
import { geolocateMapCenter, fetchDefaultLocationFromZipcode } from './locationThunks';
import { handleSetDefaultLocation, getStoredLocationHistory } from './locationUtils';

const storedLocationHistory = getStoredLocationHistory();

const initialState = {
  latLong: { latitude: null, longitude: null },
  locationInput: '', // the actual text value of the location input element
  locationResolved: false,
  locationComponents: {
    city: null,
    zip: null,
    state: null,
  },
  userDeniedLocation: false,
  fetchingGeo: false,
  geolocationResolved: false, // indicates that the locationComponents are valid via either autocomplete, history or react-geolocation fetch
  error: null,
  locationHistory: storedLocationHistory,
};

const locationSlice = createSlice({
  name: LOCATION_SLICE_NAME,
  initialState,
  reducers: {
    setDefaultLocation: handleSetDefaultLocation,

    setLocationHistory(state, action) {
      state.locationHistory = action.payload;
    },

    /* ************************** */
    /* ** autocomplete actions ** */
    /* ************************** */
    selected(state, action) {
      const { value } = action.payload;

      if (!value) return state;

      return { ...state, locationResolved: true, geolocationResolved: false, ...value };
    },

    updateLocationFromHistory(state, action) {
      // user has selected a location from history
      const selectedHistory = action.payload;

      state.latLong = selectedHistory.latLong;
      state.locationInput = selectedHistory.locationInput;
      state.locationComponents = selectedHistory.locationComponents;
      state.locationResolved = true;
      state.error = null;
    },

    /* ************************** */
    /* ** geolocation actions *** */
    /* ************************** */
    startGetGeolocation(state) {
      state.fetchingGeo = true;
      state.error = null;
    },

    handleBrowserSupportError(state, action) {
      state.fetchingGeo = false;
      state.error =
        action.payload ||
        'Geolocation is not supported on your browser, please enter city or zipcode in the location field';
    },

    updateLocationComponentsFromGeocodeResponse(state, action) {
      const { locationInput, locationComponents, latLong } = action.payload;
      state.locationInput = locationInput;
      state.locationComponents.city = locationComponents.city;
      state.locationComponents.zip = locationComponents.zip;
      state.locationComponents.state = locationComponents.state;

      state.latLong = {
        latitude: latLong.latitude,
        longitude: latLong.longitude,
      };

      state.fetchingGeo = false;
      state.userDeniedLocation = false;
      state.geolocationResolved = true;
      state.locationResolved = true;
    },
    handleGeocodeError(state, action) {
      const error = action.payload; // error from failed geocode.fromLatLong()
      state.fetchingGeo = false;
      state.userDeniedLocation = true;
      state.locationResolved = true;
      state.error = error.message || 'Failed to obtain location components from lat & long';
    },

    /* ************************** */
    /*          login             */
    /* ************************** */
  },
  extraReducers(builder) {
    // execute search
    builder.addCase(executeSearch.fulfilled, (state, action) => {
      const { request } = action.payload;
      state.locationHistory.unshift({
        id: hashForKey(
          JSON.stringify([
            request.location.coordinates.latitude,
            request.location.coordinates.longitude,
          ])
        ),
        locationInput: request.location.input,
        latLong: {
          latitude: request.location.coordinates.latitude,
          longitude: request.location.coordinates.longitude,
        },
        locationComponents: {
          city: request.location.city,
          state: request.location.state,
          zip: request.location.zip || null,
        },
      });
    });

    builder.addCase(clearSearchForm, (state) => {
      state.locationInput = '';
    });

    builder
      .addCase(geolocateMapCenter.pending, (state) => {
        state.fetchingGeo = true;
        state.error = null;
      })
      .addCase(geolocateMapCenter.fulfilled, (state, action) => {
        const { locationInput, locationComponents, latLong } = action.payload;
        state.locationInput = locationInput;
        state.locationComponents.city = locationComponents.city;
        state.locationComponents.zip = locationComponents.zip;
        state.locationComponents.state = locationComponents.state;

        state.latLong = {
          latitude: latLong.latitude,
          longitude: latLong.longitude,
        };

        state.locationResolved = true;
        state.geolocationResolved = false;
        state.fetchingGeo = false;
        state.error = initialState.error;
      })
      .addCase(geolocateMapCenter.rejected, (state) => {
        state.fetchingGeo = false;
      });

    builder.addCase(updateStoreFromUrl, (state, action) => {
      /* eslint-disable camelcase */
      const queryParams = action.payload || {};

      const { location_input, location, city, state: stateName, zip } = queryParams;

      if (!location) return;

      state.latLong = location;

      if (city && stateName) {
        state.locationComponents = {
          city: city,
          state: stateName,
          zip: zip || null,
        };
        state.locationResolved = true;
      }

      if (location_input) {
        state.locationInput = location_input;
      } else if (city && stateName) {
        state.locationInput = `${city}, ${stateName}`;
      } else {
        state.locationInput = `${location.latitude}, ${location.longitude}`;
      }
    });

    builder.addCase(userLoggedIn.fulfilled, handleSetDefaultLocation);

    builder.addCase(fetchDefaultLocationFromZipcode.fulfilled, (state, action) => {
      const { locationInput, latLong, locationComponents } = action.payload;
      state.latLong = latLong;
      state.locationComponents = locationComponents;
      state.locationInput = locationInput;
      state.locationResolved = true;

      state.locationHistory.unshift({
        id: hashForKey(JSON.stringify([latLong.latitude, latLong.longitude])),
        locationInput: locationInput,
        latLong: {
          latitude: latLong.latitude,
          longitude: latLong.longitude,
        },
        locationComponents: {
          city: locationComponents.city,
          state: locationComponents.state,
          zip: locationComponents.zip || null,
        },
      });
    });

    builder.addCase(fetchDefaultLocationFromZipcode.rejected, handleSetDefaultLocation);

    builder.addCase(startOver, (state, action) => {
      const { lastLocation = {} } = action.payload;
      const { locationInput, latLong, locationComponents } = lastLocation;

      if (validateLatLong(latLong?.latitude, latLong?.longitude)) {
        state.latLong = latLong;

        if (locationComponents?.city && locationComponents?.state) {
          state.locationInput =
            locationInput || `${locationComponents.city}, ${locationComponents.state}`;
          state.locationComponents = locationComponents;
          state.locationResolved = true;
        }
      }
    });

    builder.addMatcher(isAnyOf(specialtySearchInPg, goToHomepage), (state, action) => {
      const { city, stateAbbreviation, location = {} } = action.payload;

      const hasValidLocation = Boolean(
        city && stateAbbreviation && location?.latitude && location?.longitude
      );

      if (hasValidLocation) {
        state.locationInput = `${city}, ${stateAbbreviation}` || '';
        state.latLong = {
          latitude: location.latitude,
          longitude: location.longitude,
        };
        state.locationComponents = {
          zip: null,
          city: city,
          state: stateAbbreviation,
        };
        state.locationResolved = true;
      }
    });
  },
});

export const {
  setDefaultLocation,
  setLocationHistory,
  selected,
  updateLocationFromHistory,
  startGetGeolocation,
  updateLocationComponentsFromGeocodeResponse,
  handleGeocodeError,
} = locationSlice.actions;
export default locationSlice;
